import { type Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useConfigs } from '../queries';

/** 배송이 출발할 수 있는 날인지. 주말(토·일)과 배송 휴무일은 제외한다. */
const isDeliveryWorkingDay = (
  day: Dayjs,
  holidays: Record<string, boolean>,
): boolean => {
  const dayOfWeek = day.day();
  if (dayOfWeek === 0 || dayOfWeek === 6) return false;

  return !holidays[day.format('YYYY-MM-DD')];
};

/**
 * `target` **다음**의 가장 가까운 영업일.
 *
 * 주말이나 휴무일을 만나면 영업일이 나올 때까지 하루씩 밀어낸다.
 *
 * 이전 구현은 요일별로 더할 일수를 고정(금 +3 / 토 +2 / 그 외 +1)했는데, 금요일 분기가
 * 주석 처리되면서 금요일 마감 건이 "토요일 출발" 로 안내되는 문제가 있었다. 또 휴무일은
 * 첫 후보 하루만 검사해서 연휴(휴무가 연속으로 이어지는 경우)를 건너뛰지 못했다.
 * 후보를 하루씩 검사하는 방식이라 두 경우 모두 자연스럽게 처리된다.
 */
const findNextWorkingDay = (
  target: Dayjs,
  holidays: Record<string, boolean>,
): Dayjs => {
  const next = target.add(1, 'day');

  return isDeliveryWorkingDay(next, holidays)
    ? next
    : findNextWorkingDay(next, holidays);
};

export const useGetNextWorkingDay = (date: Dayjs | null) => {
  const { data: configs } = useConfigs();

  const holidays = useMemo<Record<string, boolean>>(() => {
    return (
      configs?.delivery_holidays.reduce((acc, cur) => {
        return {
          ...acc,
          [cur]: true,
        };
      }, {}) ?? {}
    );
  }, [configs?.delivery_holidays]);

  return useMemo(() => {
    if (date === null) return null;

    return findNextWorkingDay(date, holidays);
  }, [date, holidays]);
};
